// ─────────────────────────────────────────────────────────
//  gestureForModal.js
//
//  Gesture 1 – "pinch-hold" (middleFinger + thumb ≤ threshold for
//               PINCH_HOLD_MS) → toggles the image modal.
//
//  Gesture 2 – pinkyFinger fingertip hovering over #myImg (in the
//               page, NOT the modal) → shows an overlay
//               caption; moving away hides it.
// ─────────────────────────────────────────────────────────

// ── Tuneable constants ────────────────────────────────────
const PINCH_THRESHOLD = 38; // px – middleFinger-tip to thumb-tip
const PINCH_HOLD_MS = 420; // ms – must hold before firing
const MODAL_COOLDOWN_MS = 900; // ms – debounce between toggles

// ── State ─────────────────────────────────────────────────
let pinchState = {
  active: false,
  startTime: 0,
  fired: false,
};
let lastModalToggleTime = 0;
let modalOpen = false;

// ── DOM refs (resolved lazily so the module can be imported early) ──
function getModal() {
  return document.getElementById('myModal');
}
function getCaption() {
  return document.getElementById('caption');
}
function getModalImg() {
  return document.querySelector('.modal-content');
}
function getThumbImg() {
  return document.getElementById('myImg');
}

// ── Ensure the image-hover overlay exists ────────────────
function ensureHoverOverlay() {
  let ov = document.getElementById('gesture-img-overlay');
  if (!ov) {
    ov = document.createElement('div');
    ov.id = 'gesture-img-overlay';
    ov.textContent = 'Pinch & hold to open 🤌 ';
    // Inline styles so no extra CSS file is needed (merged into styles.css below)
    Object.assign(ov.style, {
      position: 'absolute',
      bottom: '0',
      left: '0',
      right: '0',
      background: 'linear-gradient(transparent, rgba(0,0,0,0.75))',
      color: '#fff',
      padding: '18px 14px 14px',
      fontSize: 'clamp(0.75rem, 2vw, 1rem)',
      borderRadius: '0 0 10px 10px',
      textAlign: 'center',
      opacity: '0',
      transition: 'opacity 0.25s ease',
      pointerEvents: 'none',
    });

    // The image-container must be position:relative for absolute children
    const container = document.querySelector('.image-container');
    if (container) {
      container.style.position = 'relative';
      container.appendChild(ov);
    }
  }
  return ov;
}

// ─────────────────────────────────────────────────────────
//  Main export – call once per detection frame
//  @param prediction  handpose prediction object
//  @param screenX     cursor X in CSS pixels (already mapped)
//  @param screenY     cursor Y in CSS pixels
// ─────────────────────────────────────────────────────────
export function detectModalGestures(prediction, screenX, screenY) {
  const now = Date.now();

  // ── Fingertip positions (raw, camera space) ───────────
  const pinkyTip = prediction.annotations.pinky[3];
  const thumbTip = prediction.annotations.thumb[3];

  const pinchDist = Math.hypot(pinkyTip[0] - thumbTip[0], pinkyTip[1] - thumbTip[1]);

  const isPinching = pinchDist <= PINCH_THRESHOLD;

  // ── 1. PINCH-HOLD → toggle modal ─────────────────────
  if (isPinching) {
    if (!pinchState.active) {
      pinchState.active = true;
      pinchState.startTime = now;
      pinchState.fired = false;
    }

    const held = now - pinchState.startTime;

    // Show a "charging" visual on the cursor
    const cursor = document.getElementById('virtual-cursor');
    if (cursor) {
      const progress = Math.min(held / PINCH_HOLD_MS, 1);
      cursor.style.setProperty('--pinch-progress', progress.toFixed(2));
      cursor.classList.add('pinching');
    }

    if (!pinchState.fired && held >= PINCH_HOLD_MS) {
      if (now - lastModalToggleTime > MODAL_COOLDOWN_MS) {
        toggleModal();
        lastModalToggleTime = now;
      }
      pinchState.fired = true;
    }
  } else {
    // Released – clean up
    pinchState.active = false;
    pinchState.fired = false;
    const cursor = document.getElementById('virtual-cursor');
    if (cursor) cursor.classList.remove('pinching');
  }

  // ── 2. INDEX HOVER → image caption overlay ───────────
  const overlay = ensureHoverOverlay();
  const thumbImgEl = getThumbImg();

  if (thumbImgEl && overlay && !modalOpen) {
    const rect = thumbImgEl.getBoundingClientRect();
    const inside =
      screenX >= rect.left &&
      screenX <= rect.right &&
      screenY >= rect.top &&
      screenY <= rect.bottom;

    overlay.style.opacity = inside ? '1' : '0';
  }
}

// ─────────────────────────────────────────────────────────
//  Modal helpers
// ─────────────────────────────────────────────────────────
function toggleModal() {
  const modal = getModal();
  const srcImg = getThumbImg();
  const modalImg = getModalImg();
  const caption = getCaption();
  if (!modal) return;

  if (!modalOpen) {
    // Populate and open
    if (modalImg && srcImg) modalImg.src = srcImg.src;
    /*     if (caption && srcImg) caption.innerHTML = srcImg.alt; */
    modal.style.display = 'block';
    modalOpen = true;
    console.log('[gesture] Modal OPENED');
  } else {
    modal.style.display = 'none';
    modalOpen = false;
    console.log('[gesture] Modal CLOSED');
  }
}

// Also wire up the existing close-button & outside-click behaviour
document.addEventListener('DOMContentLoaded', () => {
  const modal = getModal();
  if (!modal) return;

  // Close button (×)
  modal.querySelector('.close')?.addEventListener('click', () => {
    modal.style.display = 'none';
    modalOpen = false;
  });

  // Click outside image
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      modal.style.display = 'none';
      modalOpen = false;
    }
  });

  // Thumbnail click (mouse / touch fallback)
  getThumbImg()?.addEventListener('click', () => {
    if (!modalOpen) toggleModal();
  });
});
